import { MutableRefObject, useCallback, useEffect, useRef } from 'react';

import { difference } from '../../../../../../../common/maths/geometry/point';
import { Point } from '../../../../../../../common/maths/geometry/pointTypes';
import { RectSize } from '../../../../../../../common/maths/geometry/rect/rectTypes';
import { useMeasuredElementRect } from '../../../../../../utils/dom/useMeasuredElementRect';
import { getViewportCentrePoint } from '../../../utils/imageModalZoomGeometryUtils';

type UseImageModalV2ViewportGeometryResult = {
    getMeasuredViewportCentrePoint: () => Point | null;
    getViewportPoint: (clientPoint: Point) => Point | null;
    zoomViewportRef: MutableRefObject<HTMLDivElement | null>;
};

/**
 * Returns whether a measured viewport size can be used for zoom geometry.
 */
const hasMeasuredViewportSize = (viewportSize: RectSize): boolean => viewportSize.width > 0 && viewportSize.height > 0;

/**
 * Reads the viewport origin in client coordinates from a DOM element.
 */
const getElementViewportOrigin = (element: HTMLElement | null): Point | null => {
    const viewportRect = element?.getBoundingClientRect();
    if (!viewportRect) return null;

    return { x: viewportRect.left, y: viewportRect.top };
};

/**
 * Converts a client coordinate into a viewport-local point.
 */
const getViewportPointFromOrigin = (clientPoint: Point, viewportOrigin: Point): Point =>
    difference(viewportOrigin, clientPoint);

/**
 * Owns the V2 image modal viewport measurement and client-to-viewport coordinate conversion.
 */
export const useImageModalV2ViewportGeometry = (
    setViewportSize: (viewportSize: RectSize) => void,
): UseImageModalV2ViewportGeometryResult => {
    const zoomViewportRef = useRef<HTMLDivElement | null>(null);
    const viewportOriginRef = useRef<Point | null>(null);
    const measuredViewportSize = useMeasuredElementRect(zoomViewportRef);

    /**
     * Stores the latest viewport origin for wheel and pointer event coordinate conversion.
     */
    const syncViewportOrigin = useCallback(() => {
        viewportOriginRef.current = getElementViewportOrigin(zoomViewportRef.current);
    }, []);

    /**
     * Converts a client point into viewport coordinates when the viewport origin is known.
     */
    const getViewportPoint = useCallback((clientPoint: Point): Point | null => {
        const viewportOrigin = viewportOriginRef.current;
        if (!viewportOrigin) return null;

        return getViewportPointFromOrigin(clientPoint, viewportOrigin);
    }, []);

    /**
     * Returns the measured viewport centre once the viewport has a usable size.
     */
    const getMeasuredViewportCentrePoint = useCallback((): Point | null => {
        if (!hasMeasuredViewportSize(measuredViewportSize)) return null;

        return getViewportCentrePoint(measuredViewportSize);
    }, [measuredViewportSize]);

    /**
     * Syncs the shared zoom viewport size and origin after viewport measurement changes.
     */
    useEffect(() => {
        if (!hasMeasuredViewportSize(measuredViewportSize)) return;

        setViewportSize(measuredViewportSize);
        syncViewportOrigin();
    }, [measuredViewportSize, setViewportSize, syncViewportOrigin]);

    return {
        getMeasuredViewportCentrePoint,
        getViewportPoint,
        zoomViewportRef,
    };
};
